import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
interface HeroSectionProps {
  isDark: boolean;
}
// The hero image (the LCP element on the home page). We serve two crops so the
// browser only downloads the one it needs: a wide landscape shot on tablet/
// desktop, and a lighter, taller crop on phones.
const HERO_IMAGE_DESKTOP = 'https://cdn.magicpatterns.com/uploads/7pbR7cAfBZEjKm75n961a2/Untitled_design-2.jpg';
const HERO_IMAGE_MOBILE = 'https://cdn.magicpatterns.com/uploads/5p8SAadpusRjjUJUmYipsD/Untitled_design-3.jpg';
export function HeroSection({
  isDark
}: HeroSectionProps) {
  return <section className="relative min-h-screen flex items-center justify-center pt-20 pb-16 overflow-hidden bg-[#f8f9fb] dark:bg-[#0f1629] transition-colors duration-300" data-id="element-108" data-mp="2mtnlqo73xbkj">
      {/* Background Effects — desktop only. A 140px blur radius across a 500px
          element is one of the most expensive things a low-end phone can be
          asked to paint, and on a narrow screen these blobs are largely cropped
          out of view anyway. */}
      <div className="hidden md:block absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-accent-500/8 dark:bg-accent-500/15 rounded-full blur-[140px] -z-10" data-id="element-109" data-mp="2be6voeonzyui"></div>
      <div className="hidden md:block absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-primary-500/8 dark:bg-primary-500/15 rounded-full blur-[140px] -z-10" data-id="element-110" data-mp="2p6mqwtbbqskv"></div>

      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8" data-id="element-111" data-mp="2z2gdl5irc2gd">
        {/* Hero tile with image background */}
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 dark:border-white/10 shadow-xl shadow-slate-900/5 dark:shadow-black/30" data-id="element-112" data-mp="2u3wlendklufk">
          {/* Background image — eager + high priority as the LCP element.
           <picture> lets the browser fetch only the crop it needs:
           the wide desktop shot from md (768px) up, the lighter phone crop below. */}
          <picture data-id="element-113" data-mp="18sdb4nl5ec5n">
            <source media="(min-width: 768px)" srcSet={HERO_IMAGE_DESKTOP} data-id="element-114" data-mp="18swn7no4a6br" />
            <img src={HERO_IMAGE_MOBILE} alt="A mortgage strategist mapping out a property portfolio plan on a whiteboard with a client" loading="eager" decoding="sync" {...{
            fetchpriority: 'high'
          }} className="absolute inset-0 w-full h-full object-cover" data-id="element-115" data-mp="38umlwdfwqipn" />
          </picture>
          {/* Readability overlay */}
          <div className="absolute inset-0 bg-white/70 dark:bg-[#0f1629]/75" data-id="element-116" data-mp="3rqcoa1rr5h42"></div>

          {/* Content */}
          <div className="relative z-10 text-center px-6 sm:px-10 lg:px-16 py-16 sm:py-20 lg:py-28" data-id="element-117" data-mp="5cupmmuqncln">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none backdrop-blur-sm mb-8" data-id="element-118" data-mp="abz15k54xmf5">
              <span className="flex h-2 w-2 rounded-full bg-primary-500 animate-pulse" data-id="element-119" data-mp="jdf7ngstx4e3"></span>
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300" data-id="element-120" data-mp="23uod0x5xbtud">
                Investor & Mortgage Broker
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-6 leading-[1.1]" data-id="element-121" data-mp="1usyg5bt9w2p7">
              Smarter Property <br className="hidden md:block" data-id="element-122" data-mp="1pwpztv3ca3gg" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-primary-500" data-id="element-123" data-mp="2ml5ug5lb330f">
                Starts Here!
              </span>
            </h1>

            {/* Subheadline */}
            <p className="mt-4 text-xl md:text-2xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-12 leading-relaxed font-light" data-id="element-124" data-mp="2drkhmrspbvtn">
              Whether you're buying your first home or your next investment, we
              help you structure it properly from the start.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-125" data-mp="31gvj6bhvlu8u">
              <Link to="/#contact" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold text-lg hover:shadow-[0_0_30px_rgba(20,184,166,0.35)] transition-all flex items-center justify-center gap-2 group hover:scale-[1.02]" data-id="element-126" data-mp="2l148z5yxbtbd">
                Start Your Strategy
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-127" data-mp="34rpqie8k1bxy" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}